import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { db, isConfigured } from '../firebaseConfig';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    project_title: ''
  });

  useEffect(() => {
    if (isConfigured) {
      fetchClients();
    }
  }, []);

  const fetchClients = async () => {
    if (!isConfigured) return;
    try {
      const querySnapshot = await getDocs(collection(db, 'clients'));
      const clientsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Function to update project with client info
  const updateProjectWithClient = async (projectTitle, clientName, clientEmail) => {
    try {
      // Find project(s) with matching title
      const q = query(
        collection(db, 'projects'),
        where('title', '==', projectTitle)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log('No matching project found for title:', projectTitle);
        return false;
      }

      // Update all matching projects
      const updatePromises = querySnapshot.docs.map(projectDoc => 
        updateDoc(doc(db, 'projects', projectDoc.id), {
          clientName: clientName,
          clientEmail: clientEmail,
          updatedAt: new Date().toISOString()
        })
      );

      await Promise.all(updatePromises);
      console.log(`Updated ${querySnapshot.docs.length} project(s) with client info`);
      return true;
    } catch (error) {
      console.error('Error updating project:', error);
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!isConfigured) {
      alert('Firebase is not configured. Please set up your Firebase configuration first.');
      return;
    }
    
    try {
      if (editingClient) {
        await updateDoc(doc(db, 'clients', editingClient.id), {
          ...formData,
          updatedAt: new Date().toISOString()
        });
        
        // If project title exists, update the project
        if (formData.project_title && formData.project_title.trim()) {
          const updated = await updateProjectWithClient(
            formData.project_title,
            formData.client_name,
            formData.client_email
          );
          
          if (updated) {
            alert('Client updated successfully! Associated project has been updated with client info.');
          } else {
            alert('Client updated successfully! (No matching project found to update)');
          }
        } else {
          alert('Client updated successfully!');
        }
      } else {
        await addDoc(collection(db, 'clients'), {
          ...formData,
          createdAt: new Date().toISOString()
        });
        
        // If project title exists, update the project
        if (formData.project_title && formData.project_title.trim()) {
          const updated = await updateProjectWithClient(
            formData.project_title,
            formData.client_name,
            formData.client_email
          );
          
          if (updated) {
            alert('Client added successfully! Associated project has been updated with client info.');
          } else {
            alert('Client added successfully! (No matching project found to update)');
          }
        } else {
          alert('Client added successfully!');
        }
      }
      
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        project_title: ''
      });
      setShowAddForm(false);
      setEditingClient(null);
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Error saving client. Please try again.');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      client_name: client.client_name || '',
      client_email: client.client_email || '',
      client_phone: client.client_phone || '',
      project_title: client.project_title || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await deleteDoc(doc(db, 'clients', id));
        alert('Client deleted successfully!');
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Error deleting client. Please try again.');
      }
    }
  };

  const cancelEdit = () => {
    setShowAddForm(false);
    setEditingClient(null);
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      project_title: ''
    });
  };

  return (
    <div className='flex min-h-screen bg-[#FFB7CE]'>
      <Sidebar />
      <div className='flex-1 p-4 pt-20 lg:pt-4 lg:pl-8'>
        <div className='max-w-6xl mx-auto'>
          {!isConfigured && (
            <div className='bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded'>
              <p className='font-bold text-sm'>⚠️ Firebase Not Configured</p>
              <p className='text-xs mt-1'>
                Please set up Firebase to enable authentication.
              </p>
            </div>
          )}
          
          <div className='flex justify-between items-center mb-6'>
            <h1 className='text-3xl font-bold text-white'>CLIENTS</h1>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200'
              disabled={!isConfigured}
            >
              {showAddForm ? 'CANCEL' : 'ADD CLIENT'}
            </button>
          </div>

          {showAddForm && (
            <div className='bg-white rounded-lg p-6 mb-6 shadow-lg'>
              <h2 className='text-2xl font-bold text-[#89CFF0] mb-6'>
                {editingClient ? 'EDIT CLIENT' : 'ADD CLIENT'}
              </h2>
              <div className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>CLIENT NAME:</label>
                    <input
                      type='text'
                      name='client_name'
                      value={formData.client_name}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    />
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>EMAIL:</label>
                    <input
                      type='email'
                      name='client_email'
                      value={formData.client_email}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    />
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>PHONE:</label>
                    <input
                      type='tel'
                      name='client_phone'
                      value={formData.client_phone}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    />
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>
                      PROJECT TITLE:
                      <span className='text-sm text-gray-500 ml-2'>(Must match exactly)</span>
                    </label>
                    <input
                      type='text'
                      name='project_title'
                      value={formData.project_title}
                      onChange={handleInputChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                      placeholder='Optional - links to project'
                    />
                    {formData.project_title && (
                      <p className='text-xs text-blue-600 mt-1'>
                        ℹ️ Project with this title will be automatically updated with client info
                      </p>
                    )}
                  </div>
                </div>

                <div className='flex gap-4'>
                  <button
                    onClick={handleSubmit}
                    className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-8 py-2 rounded-lg font-medium transition-colors duration-200'
                  >
                    {editingClient ? 'UPDATE' : 'ADD'}
                  </button>
                  {editingClient && (
                    <button
                      onClick={cancelEdit}
                      className='bg-gray-400 hover:bg-gray-500 text-white px-8 py-2 rounded-lg font-medium transition-colors duration-200'
                    >
                      CANCEL
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className='bg-white rounded-lg overflow-hidden shadow-lg'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-[#FFB7CE]'>
                  <tr>
                    <th className='px-4 py-3 text-left text-white font-bold'>NAME</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>EMAIL</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>PHONE</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>PROJECT</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan='5' className='px-4 py-8 text-center text-gray-500'>
                        No clients yet. Click ADD CLIENT to create your first client.
                      </td>
                    </tr>
                  ) : (
                    clients.map(client => (
                      <tr key={client.id} className='border-b hover:bg-gray-50'>
                        <td className='px-4 py-3 font-medium'>{client.client_name}</td>
                        <td className='px-4 py-3'>{client.client_email}</td>
                        <td className='px-4 py-3'>{client.client_phone}</td>
                        <td className='px-4 py-3'>
                          {client.project_title ? (
                            <span className='bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm'>
                              {client.project_title}
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className='px-4 py-3'>
                          <div className='flex gap-2'>
                            <button
                              onClick={() => handleEdit(client)}
                              className='bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors duration-200'
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(client.id)}
                              className='bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors duration-200'
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}